import ReportSectionCard from './shared/ReportSectionCard.component'
import ReportSectionHeading from '@components/pages/Reports/shared/ReportSectionHeading.component'
import React, { useMemo, useState } from 'react'
import ReportViewTabs from '@components/pages/Reports/shared/ReportViewTabs.component'
import DashboardSectionEmptyState from '@components/pages/Dashboard/DashboardSectionEmptyState.component'
import ReportBundleStickyTopWhenExpanded from '@components/pages/Reports/shared/ReportBundleStickyTopWhenExpanded.component'
import ReportFilterToolbar from '@components/pages/Reports/shared/ReportFilterToolbar.component'
import ReportKpiGrid from '@components/pages/Reports/shared/ReportKpiGrid.component'
import ReportPageHeader from '@components/pages/Reports/shared/ReportPageHeader.component'
import { REPORTS_DISTRIBUTION_VIEW_TABS } from '@constants/Reports/patientBaseHealth.constants'
import {
  CLINICAL_MIX_TREATMENT_PROFILE_FILTERS,
  CLINICAL_MIX_TREATMENT_PROFILE_KPI_DEFINITIONS,
} from '@constants/Reports/clinicalMixTreatmentProfile.constants'
import {
  ReportsDistributionView,
  ReportsKpiMetric,
} from '@dataTypes/reports.type'
import { useClinicalMixTreatmentProfile } from '@hooks/reports/useClinicalMixTreatmentProfile'
import { useReportFinanceUiEffects } from '@hooks/reports/useReportFinanceUiEffects'
import type { ReportPeriodBucket } from '@services/api/reports/finance.api'
import {
  hasNonZeroDashboardValues,
  shouldShowDashboardSectionEmpty,
} from '@utils/dashboardSectionEmpty.util'
import {
  computeDynamicCountAxis,
  formatKpiPercent,
} from '@utils/reports/revenueRealizationFinance.util'
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from '@components/pages/Reports/shared/ReportDesignSystemCharts.component'

const REPORT_CHART_AXIS_TICK = { fontSize: 12, fill: 'var(--gray-500)' }
const REPORT_CHART_TOOLTIP_STYLE = {
  borderRadius: 8,
  border: '1px solid var(--gray-200)',
  fontSize: 12,
}
const REPORT_CHART_GRID_STROKE = 'var(--gray-100)'
const REPORT_BAR_CHART_MARGIN = { top: 24, right: 12, left: 4, bottom: 0 }
const REPORT_BAR_CATEGORY_GAP = '16%'
const REPORT_YAXIS_WIDTH = 48
const REPORT_PERCENTAGE_Y_AXIS = {
  domain: [0, 100] as [number, number],
  ticks: [0, 20, 40, 60, 80, 100],
  tickFormatter: (v: number) => `${v}%`,
}

function formatKpiCount(value: number | undefined): string {
  if (value === undefined || !Number.isFinite(value)) return '—'
  return Math.round(value).toLocaleString('en-US')
}

function buildCountSeries(
  rows: ReportPeriodBucket[],
  view: ReportsDistributionView,
): Array<{ month: string; barValue: number; topLabel: string }> {
  if (view === 'Count') {
    return rows.map((row) => ({
      month: row.label,
      barValue: row.value,
      topLabel: row.value.toLocaleString(),
    }))
  }
  const total = rows.reduce((sum, r) => sum + r.value, 0) || 1
  return rows.map((row) => {
    const pct = Math.round((row.value / total) * 1000) / 10
    return {
      month: row.label,
      barValue: pct,
      topLabel: `${pct}%`,
    }
  })
}

function ClinicalMixTreatmentProfileReport(): React.ReactElement {
  const [crownView, setCrownView] = useState<ReportsDistributionView>('Count')
  const [srpView, setSrpView] = useState<ReportsDistributionView>('Count')
  const [fillingView, setFillingView] =
    useState<ReportsDistributionView>('Count')
  const [extractionView, setExtractionView] =
    useState<ReportsDistributionView>('Count')
  const [reportSaveViewAction, setReportSaveViewAction] =
    useState<JSX.Element | null>(null)
  const [reportGeneratePDFAction, setReportGeneratePDFAction] =
    useState<JSX.Element | null>(null)
  // `reportFiltersAndMetrics` is the source of truth for the filters and metrics of the currently selected report view.
  // Use this for all data queries and any logic that depends on the selected report view.
  const [reportFiltersAndMetrics, setReportFiltersAndMetrics] = useState<{
    filters: Record<string, boolean>
    metrics: Record<string, boolean>
  } | null>(null)
  const { loading, error, data, bucketLabel } = useClinicalMixTreatmentProfile(
    (reportFiltersAndMetrics?.filters ?? {}) as unknown as Record<
      string,
      string
    >,
  )
  useReportFinanceUiEffects({
    loading,
    error,
    errorMessage:
      'Failed to load clinical mix treatment profile data. Please try again.',
  })

  const kpiItems = useMemo((): ReportsKpiMetric[] => {
    if (!data) {
      return CLINICAL_MIX_TREATMENT_PROFILE_KPI_DEFINITIONS.map((def) => ({
        id: def.id,
        title: def.title,
        footer: def.footer,
        value: loading ? '…' : '—',
      }))
    }

    const valueById: Record<string, number> = {
      'total-procedures': data.totalProcedures.value,
      'crown-percentage': data.crownPercentage.value,
      'srp-percentage': data.srpPercentage.value,
      'high-value-procedure-percentage':
        data.highValueProcedurePercentage.value,
    }

    return CLINICAL_MIX_TREATMENT_PROFILE_KPI_DEFINITIONS.map((def) => ({
      id: def.id,
      title: def.title,
      footer: def.footer,
      value:
        def.format === 'percent'
          ? formatKpiPercent(valueById[def.id])
          : formatKpiCount(valueById[def.id]),
    }))
  }, [data, loading])

  const crownChartData = useMemo(
    () => buildCountSeries(data?.crownProcedures.buckets ?? [], crownView),
    [data, crownView],
  )
  const crownYAxis = useMemo(() => {
    if (crownView === 'Percentage') return REPORT_PERCENTAGE_Y_AXIS
    return computeDynamicCountAxis(crownChartData.map((row) => row.barValue))
  }, [crownView, crownChartData])

  const srpChartData = useMemo(
    () => buildCountSeries(data?.srpProcedures.buckets ?? [], srpView),
    [data, srpView],
  )
  const srpYAxis = useMemo(() => {
    if (srpView === 'Percentage') return REPORT_PERCENTAGE_Y_AXIS
    return computeDynamicCountAxis(srpChartData.map((row) => row.barValue))
  }, [srpView, srpChartData])

  const fillingChartData = useMemo(
    () => buildCountSeries(data?.fillingProcedures.buckets ?? [], fillingView),
    [data, fillingView],
  )
  const fillingYAxis = useMemo(() => {
    if (fillingView === 'Percentage') return REPORT_PERCENTAGE_Y_AXIS
    return computeDynamicCountAxis(fillingChartData.map((row) => row.barValue))
  }, [fillingView, fillingChartData])

  const extractionChartData = useMemo(
    () =>
      buildCountSeries(
        data?.extractionProcedures.buckets ?? [],
        extractionView,
      ),
    [data, extractionView],
  )
  const extractionYAxis = useMemo(() => {
    if (extractionView === 'Percentage') return REPORT_PERCENTAGE_Y_AXIS
    return computeDynamicCountAxis(
      extractionChartData.map((row) => row.barValue),
    )
  }, [extractionView, extractionChartData])

  const renderChart = (
    chartData: ReturnType<typeof buildCountSeries>,
    axis: ReturnType<typeof computeDynamicCountAxis>,
    view: ReportsDistributionView,
  ): React.ReactElement => (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        data={chartData}
        margin={REPORT_BAR_CHART_MARGIN}
        barCategoryGap={REPORT_BAR_CATEGORY_GAP}
      >
        <CartesianGrid
          vertical={false}
          strokeDasharray="0"
          stroke={REPORT_CHART_GRID_STROKE}
        />
        <XAxis
          dataKey="month"
          axisLine={false}
          tickLine={false}
          tick={REPORT_CHART_AXIS_TICK}
        />
        <YAxis
          domain={axis.domain}
          ticks={axis.ticks}
          tickFormatter={axis.tickFormatter}
          axisLine={false}
          tickLine={false}
          tick={REPORT_CHART_AXIS_TICK}
          width={REPORT_YAXIS_WIDTH}
        />
        <Tooltip
          formatter={(value: number) =>
            view === 'Count' ? value.toLocaleString() : `${value}%`
          }
          contentStyle={REPORT_CHART_TOOLTIP_STYLE}
        />
        <Bar
          dataKey="barValue"
          fill="var(--blue-400)"
          radius={[8, 8, 8, 8]}
          barSize={100}
        />
      </BarChart>
    </ResponsiveContainer>
  )

  const kpiEmpty = shouldShowDashboardSectionEmpty(
    loading,
    hasNonZeroDashboardValues(
      data?.totalProcedures.value,
      data?.crownPercentage.value,
      data?.srpPercentage.value,
      data?.highValueProcedurePercentage.value,
    ),
  )
  const crownEmpty = shouldShowDashboardSectionEmpty(
    loading,
    (data?.crownProcedures?.buckets ?? []).some((row) =>
      hasNonZeroDashboardValues(row.value),
    ),
  )
  const srpEmpty = shouldShowDashboardSectionEmpty(
    loading,
    (data?.srpProcedures?.buckets ?? []).some((row) =>
      hasNonZeroDashboardValues(row.value),
    ),
  )
  const fillingEmpty = shouldShowDashboardSectionEmpty(
    loading,
    (data?.fillingProcedures?.buckets ?? []).some((row) =>
      hasNonZeroDashboardValues(row.value),
    ),
  )
  const extractionEmpty = shouldShowDashboardSectionEmpty(
    loading,
    (data?.extractionProcedures?.buckets ?? []).some((row) =>
      hasNonZeroDashboardValues(row.value),
    ),
  )

  return (
    <div className="reports-patient-base-health reports-treatment-conversion-funnel reports-clinical-mix-treatment-profile">
      <ReportBundleStickyTopWhenExpanded>
        <ReportPageHeader title="Clinical Mix & Treatment Profile">
          {reportSaveViewAction}
          {reportGeneratePDFAction}
        </ReportPageHeader>

        <ReportFilterToolbar
          filters={CLINICAL_MIX_TREATMENT_PROFILE_FILTERS}
          onReportSaveViewActionChange={setReportSaveViewAction}
          onReportGeneratePDFActionChange={setReportGeneratePDFAction}
          onReportFiltersAndMetricsChange={setReportFiltersAndMetrics}
        />
      </ReportBundleStickyTopWhenExpanded>

      {kpiEmpty ? (
        <DashboardSectionEmptyState />
      ) : (
        <ReportKpiGrid items={kpiItems} />
      )}

      <div className="reports-treatment-conversion-funnel__stack">
        <div className="reports-treatment-conversion-funnel__grid">
          <ReportSectionCard className="reports-patient-base-health__chart-card">
            <div className="reports-patient-value-revenue__column">
              <div className="reports-patient-base-health__chart-head">
                <ReportSectionHeading>
                  Crown Procedures ({bucketLabel})
                </ReportSectionHeading>
                {crownEmpty ? null : (
                  <ReportViewTabs
                    options={REPORTS_DISTRIBUTION_VIEW_TABS}
                    value={crownView}
                    onChange={(value) =>
                      setCrownView(value as ReportsDistributionView)
                    }
                    className="reports-patient-base-health__view-group"
                  />
                )}
              </div>
              {crownEmpty ? (
                <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
              ) : (
                <div className="reports-treatment-conversion-funnel__chart">
                  {renderChart(crownChartData, crownYAxis, crownView)}
                </div>
              )}
            </div>
          </ReportSectionCard>

          <ReportSectionCard className="reports-patient-base-health__chart-card">
            <div className="reports-patient-value-revenue__column">
              <div className="reports-patient-base-health__chart-head">
                <ReportSectionHeading>
                  SRP Procedures ({bucketLabel})
                </ReportSectionHeading>
                {srpEmpty ? null : (
                  <ReportViewTabs
                    options={REPORTS_DISTRIBUTION_VIEW_TABS}
                    value={srpView}
                    onChange={(value) =>
                      setSrpView(value as ReportsDistributionView)
                    }
                    className="reports-patient-base-health__view-group"
                  />
                )}
              </div>
              {srpEmpty ? (
                <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
              ) : (
                <div className="reports-treatment-conversion-funnel__chart">
                  {renderChart(srpChartData, srpYAxis, srpView)}
                </div>
              )}
            </div>
          </ReportSectionCard>
        </div>

        <div className="reports-treatment-conversion-funnel__grid">
          <ReportSectionCard className="reports-patient-base-health__chart-card">
            <div className="reports-patient-value-revenue__column">
              <div className="reports-patient-base-health__chart-head">
                <ReportSectionHeading>
                  Filling Procedures ({bucketLabel})
                </ReportSectionHeading>
                {fillingEmpty ? null : (
                  <ReportViewTabs
                    options={REPORTS_DISTRIBUTION_VIEW_TABS}
                    value={fillingView}
                    onChange={(value) =>
                      setFillingView(value as ReportsDistributionView)
                    }
                    className="reports-patient-base-health__view-group"
                  />
                )}
              </div>
              {fillingEmpty ? (
                <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
              ) : (
                <div className="reports-treatment-conversion-funnel__chart">
                  {renderChart(fillingChartData, fillingYAxis, fillingView)}
                </div>
              )}
            </div>
          </ReportSectionCard>

          <ReportSectionCard className="reports-patient-base-health__chart-card">
            <div className="reports-patient-value-revenue__column">
              <div className="reports-patient-base-health__chart-head">
                <ReportSectionHeading>
                  Extraction Procedures ({bucketLabel})
                </ReportSectionHeading>
                {extractionEmpty ? null : (
                  <ReportViewTabs
                    options={REPORTS_DISTRIBUTION_VIEW_TABS}
                    value={extractionView}
                    onChange={(value) =>
                      setExtractionView(value as ReportsDistributionView)
                    }
                    className="reports-patient-base-health__view-group"
                  />
                )}
              </div>
              {extractionEmpty ? (
                <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
              ) : (
                <div className="reports-treatment-conversion-funnel__chart">
                  {renderChart(
                    extractionChartData,
                    extractionYAxis,
                    extractionView,
                  )}
                </div>
              )}
            </div>
          </ReportSectionCard>
        </div>
      </div>
    </div>
  )
}

export default ClinicalMixTreatmentProfileReport
